import {
  DuctedCompatibilityEntry,
  DuctedResultSet
} from 'features/ducted-compatibility';
import { DuctedSystemTypeSelection } from 'features/ui/ui-slice';
import { facetCodeByLabel } from 'utils/ducted-rail-selections';
import {
  DuctedFuelToken,
  systemTypeCodeFuel
} from 'utils/kept-system-type-annotation';
import { SystemSortOption } from 'utils/system-sort';
import { scopedDuctedTypeCodes } from './ducted-facet-merge';
import {
  DuctedRailRankGroupCard,
  DuctedRailRankGroupModel,
  DuctedRailRankGroupTail,
  DuctedRailSubSectionModel
} from './ducted-rail-sections-types';

export const FEATURED_RANK_GROUP_COUNT = 3;

export const featuredRankGroupCard = (
  group: Pick<DuctedRailRankGroupModel, 'cards'>
): DuctedRailRankGroupCard | null => group.cards[0] ?? null;

// Lead cards on screen for one type. Both result-set leads show before
// "Show more", so revealing the tail does not add representative cards.
export const visibleFeaturedCardCount = (
  group: Pick<DuctedRailRankGroupModel, 'cards'>
): number =>
  group.cards.filter((card) => card.representative?.combination != null).length;

export const OTHER_RANK_GROUP_LABEL = 'Other systems';

const OTHER_RANK_GROUP_KEY_SUFFIX = '#other';

export type DuctedRankGroupInputs = {
  systemUniqueId: string;
  subSections: DuctedRailSubSectionModel[];
  priorityCodes: string[];
  summaryLabelByCode: Map<string, string>;
  systemTypeSelections: DuctedSystemTypeSelection[];
  fuelSelections: DuctedFuelToken[];
  // The Choose Systems "Sort by": under 'recommended' the groups follow the
  // recommendation priority; under a metric sort they follow the server's
  // order of their lead rows.
  systemSort: SystemSortOption;
};

type SummaryLabelRecord =
  | {
      kept?: { systemTypes: Array<{ code: string; label: string }> };
      alternatives?: { systemTypes: Array<{ code: string; label: string }> };
    }
  | undefined;

type TailBuckets = Map<
  DuctedResultSet,
  Map<string | null, Record<string, unknown>[]>
>;

export const ductedRankGroupKey = (
  systemUniqueId: string,
  code: string | null
): string => `${systemUniqueId}:${code ?? OTHER_RANK_GROUP_KEY_SUFFIX}`;

export const summaryLabelsFor = (
  record: SummaryLabelRecord
): Map<string, string> => {
  const labels = new Map<string, string>();
  [record?.alternatives, record?.kept].forEach((entry) => {
    entry?.systemTypes.forEach((systemType) => {
      if (!labels.has(systemType.code)) {
        labels.set(systemType.code, systemType.label);
      }
    });
  });
  return labels;
};

const entryHasData = (entry: DuctedCompatibilityEntry | undefined): boolean =>
  entry !== undefined &&
  (entry.status === 'loaded' ||
    entry.systemTypes.length > 0 ||
    entry.totalCount > 0);

const bucketTails = (subSections: DuctedRailSubSectionModel[]): TailBuckets => {
  const buckets: TailBuckets = new Map();
  subSections.forEach((subSection) => {
    const byCode = new Map<string | null, Record<string, unknown>[]>();
    const codeByLabel = facetCodeByLabel(subSection.entry?.systemTypes ?? []);
    subSection.tail.forEach((combination) => {
      const code =
        codeByLabel.get((combination.SystemTypeLabel as string) ?? '') ?? null;
      const rows = byCode.get(code) ?? [];
      rows.push(combination);
      byCode.set(code, rows);
    });
    buckets.set(subSection.resultSet, byCode);
  });
  return buckets;
};

const pushUnique = (codes: string[], seen: Set<string>, code: string) => {
  if (!seen.has(code)) {
    seen.add(code);
    codes.push(code);
  }
};

const codePassesFilters = (
  code: string,
  { subSections, systemTypeSelections, fuelSelections }: DuctedRankGroupInputs
): boolean => {
  const fuelPasses =
    fuelSelections.length === 0 ||
    fuelSelections.includes(systemTypeCodeFuel(code));
  const selectionPasses =
    systemTypeSelections.length === 0 ||
    subSections.some((subSection) =>
      scopedDuctedTypeCodes(
        systemTypeSelections,
        subSection.resultSet
      ).codes.includes(code)
    );
  return fuelPasses && selectionPasses;
};

const codeHasContent = (
  code: string,
  subSections: DuctedRailSubSectionModel[],
  buckets: TailBuckets
): boolean =>
  subSections.some((subSection) =>
    subSection.representativeEntries.some(
      (entry) => entry.systemType.code === code
    )
  ) ||
  Array.from(buckets.values()).some(
    (byCode) => (byCode.get(code)?.length ?? 0) > 0
  );

const orderedCodes = (
  inputs: DuctedRankGroupInputs,
  buckets: TailBuckets
): string[] => {
  const settled = inputs.subSections.every((subSection) =>
    entryHasData(subSection.entry)
  );
  const codes: string[] = [];
  const seen = new Set<string>();
  inputs.priorityCodes.forEach((code) => {
    const included =
      codePassesFilters(code, inputs) &&
      (settled || codeHasContent(code, inputs.subSections, buckets));
    if (included) pushUnique(codes, seen, code);
  });
  inputs.subSections.forEach((subSection) => {
    subSection.representativeEntries.forEach((entry) =>
      pushUnique(codes, seen, entry.systemType.code)
    );
  });
  buckets.forEach((byCode) => {
    byCode.forEach((rows, code) => {
      if (code !== null && rows.length > 0) pushUnique(codes, seen, code);
    });
  });
  return codes;
};

const labelFor = (
  code: string,
  { subSections, summaryLabelByCode }: DuctedRankGroupInputs
): string =>
  subSections
    .map(
      (subSection) =>
        subSection.entry?.systemTypes.find(
          (systemType) => systemType.code === code
        )?.label
    )
    .find((label) => label !== undefined) ??
  summaryLabelByCode.get(code) ??
  code;

const cardsFor = (
  code: string,
  subSections: DuctedRailSubSectionModel[]
): DuctedRailRankGroupCard[] =>
  subSections.flatMap((subSection) => {
    const match = subSection.representativeEntries.find(
      (entry) => entry.systemType.code === code
    );
    return match && subSection.entry
      ? [
          {
            resultSet: subSection.resultSet,
            entry: subSection.entry,
            systemType: match.systemType,
            representative: match.representative
          }
        ]
      : [];
  });

const tailsFor = (
  code: string | null,
  subSections: DuctedRailSubSectionModel[],
  buckets: TailBuckets
): DuctedRailRankGroupTail[] =>
  subSections.flatMap((subSection) => {
    const rows = buckets.get(subSection.resultSet)?.get(code) ?? [];
    return rows.length > 0 && subSection.entry
      ? [
          {
            resultSet: subSection.resultSet,
            entry: subSection.entry,
            combinations: rows
          }
        ]
      : [];
  });

const groupTotalCount = (
  code: string,
  { subSections, systemTypeSelections }: DuctedRankGroupInputs
): number =>
  subSections.reduce((sum, subSection) => {
    const scoped = scopedDuctedTypeCodes(
      systemTypeSelections,
      subSection.resultSet
    );
    if (scoped.active && !scoped.codes.includes(code)) return sum;
    const facet = subSection.entry?.systemTypes.find(
      (systemType) => systemType.code === code
    );
    return sum + (facet?.count ?? 0);
  }, 0);

// Where the group's lead sits in the server's order: the earliest rank among
// its cards' lead rows; a group whose leads have no row yet sorts last.
const leadRankOf = (group: Pick<DuctedRailRankGroupModel, 'cards'>): number =>
  group.cards.reduce(
    (best, card) => Math.min(best, card.representative?.rank ?? Infinity),
    Infinity
  );

// Under a metric sort the groups follow the server's order of their leads
// (the rank the response stamped), so "Price: Low to High" reads lowest
// first across types; under 'recommended' the recommendation priority order
// stands. A stable sort keeps the priority order among ties.
const orderGroupsBySort = <
  TGroup extends Pick<DuctedRailRankGroupModel, 'cards'>
>(
  groups: TGroup[],
  systemSort: SystemSortOption
): TGroup[] =>
  systemSort === 'recommended'
    ? groups
    : groups
        .map((group, index) => ({ group, index, rank: leadRankOf(group) }))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map(({ group }) => group);

export const buildDuctedRankGroups = (
  inputs: DuctedRankGroupInputs
): DuctedRailRankGroupModel[] => {
  const buckets = bucketTails(inputs.subSections);
  const groups: Array<Omit<DuctedRailRankGroupModel, 'rank'>> =
    orderGroupsBySort(
      orderedCodes(inputs, buckets).map((code) => {
        const cards = cardsFor(code, inputs.subSections);
        const tails = tailsFor(code, inputs.subSections, buckets);
        return {
          key: ductedRankGroupKey(inputs.systemUniqueId, code),
          code,
          label: labelFor(code, inputs),
          cards,
          tails,
          totalCount: groupTotalCount(code, inputs),
          empty: cards.length === 0 && tails.length === 0
        };
      }),
      inputs.systemSort
    );
  const otherTails = tailsFor(null, inputs.subSections, buckets);
  if (otherTails.length > 0) {
    groups.push({
      key: ductedRankGroupKey(inputs.systemUniqueId, null),
      code: null,
      label: OTHER_RANK_GROUP_LABEL,
      cards: [],
      tails: otherTails,
      totalCount: otherTails.reduce(
        (sum, tail) => sum + tail.combinations.length,
        0
      ),
      empty: false
    });
  }
  return groups.map((group, index) => ({ ...group, rank: index + 1 }));
};
